"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SimpleTabs, type SimpleTab } from "../SimpleTabs";
import { useERP, formatBDT, formatNum, type ERPState } from "@/lib/erp/store";
import { useToast } from "@/hooks/use-toast";
import { Truck, MapPin, User, Package, Activity, Map as MapIcon, Navigation, Crosshair, Plus, CheckCircle2, Camera, Clock, Store } from "lucide-react";

// Bangladesh district coordinates (approx, in SVG x/y of our 420x360 viewport)
const BD_COORDS: Record<string, { x: number; y: number }> = {
  "Dhaka": { x: 230, y: 200 }, "Chattogram": { x: 340, y: 270 }, "Khulna": { x: 130, y: 260 },
  "Sylhet": { x: 330, y: 110 }, "Rajshahi": { x: 100, y: 140 }, "Barishal": { x: 200, y: 280 },
  "Rangpur": { x: 180, y: 70 }, "Mymensingh": { x: 270, y: 140 }, "Comilla": { x: 300, y: 220 },
  "Cox's Bazar": { x: 380, y: 290 },
};

// C6 fix: fuzzy-match zone → district coords (handles "Dhaka Zone-1", "Dhaka HO" etc.)
function zoneToCoord(zone: string | undefined): { x: number; y: number } {
  if (!zone) return BD_COORDS.Dhaka;
  // exact match first
  if (BD_COORDS[zone]) return BD_COORDS[zone];
  // prefix match (e.g. "Dhaka Zone-1" → "Dhaka")
  const lower = zone.toLowerCase();
  for (const key of Object.keys(BD_COORDS)) {
    if (lower.startsWith(key.toLowerCase())) return BD_COORDS[key];
  }
  return BD_COORDS.Dhaka;
}

export function RealtimeTrackingPage() {
  const erp = useERP();
  const [tab, setTab] = React.useState("map");
  const tabs: SimpleTab[] = [
    { value: "map", label: "Live Map", icon: MapIcon, count: erp.shipments.filter(s => s.status === "In Transit" || s.status === "Dispatched").length },
    { value: "salesman", label: "Salesman Tracking", icon: User, count: erp.employees.filter(e => e.department === "Sales" || e.department === "Logistics").length },
    { value: "orders", label: "Realtime Orders", icon: Activity, count: erp.salesOrders.filter(o => ["Approved","Picking","Dispatched"].includes(o.status)).length },
    { value: "zones", label: "Zone Management", icon: MapPin, count: new Set(erp.customers.map(c => c.zone)).size },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold sm:text-2xl">GIS Real-time Tracking</h1>
        <p className="text-muted-foreground mt-1 text-sm">Live GPS tracking of salesmen, vehicles and active orders across Bangladesh with POD capture.</p>
      </div>
      <SimpleTabs value={tab} onValueChange={setTab} tabs={tabs} />
      {tab === "map" && <LiveMap erp={erp} />}
      {tab === "salesman" && <SalesmanTracking erp={erp} />}
      {tab === "orders" && <RealtimeOrders erp={erp} />}
      {tab === "zones" && <ZoneManagement erp={erp} />}
    </div>
  );
}

type ERP = ERPState;

// ===== Live Map with typed markers, salesman routing, and filters =====
// Customer type marker styles
const CUST_TYPE_STYLES: Record<string, { color: string; shape: "circle" | "square" | "triangle" | "diamond"; label: string }> = {
  "Distributor": { color: "#e11d48", shape: "square", label: "Distributor" },
  "Retailer": { color: "#3b82f6", shape: "circle", label: "Retailer" },
  "Corporate": { color: "#10b981", shape: "diamond", label: "Corporate" },
  "Institutional": { color: "#8b5cf6", shape: "triangle", label: "Institutional" },
};

function LiveMap({ erp }: { erp: ERP }) {
  const warehouses = erp.warehouses;
  const customers = erp.customers;
  const salesmen = erp.employees.filter(e => e.department === "Sales" || e.department === "Logistics");
  const activeShipments = erp.shipments.filter(s => s.status === "Dispatched" || s.status === "In Transit");

  // Animation tick — salesman positions move along their route
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const i = setInterval(() => setTick(t => t + 1), 1500);
    return () => clearInterval(i);
  }, []);

  // Marker type filters
  const [showWarehouses, setShowWarehouses] = React.useState(true);
  const [showDistributors, setShowDistributors] = React.useState(true);
  const [showRetailers, setShowRetailers] = React.useState(true);
  const [showCorporate, setShowCorporate] = React.useState(true);
  const [showSalesmen, setShowSalesmen] = React.useState(true);
  const [showRoutes, setShowRoutes] = React.useState(true);

  // Pick warehouse coord by index, fallback to Dhaka
  const whCoord: Record<string, { x: number; y: number; label: string }> = Object.fromEntries(
    warehouses.map((w, i) => {
      const keys = Object.keys(BD_COORDS);
      const c = BD_COORDS[keys[i % keys.length]] ?? BD_COORDS.Dhaka;
      return [w.id, { x: c.x, y: c.y, label: w.name }];
    })
  );

  // Customer coords with type information — ALL customers, not just first 12
  const custCoords = customers.map((c, i) => {
    const z = zoneToCoord(c.zone);
    const style = CUST_TYPE_STYLES[c.type] || CUST_TYPE_STYLES["Retailer"];
    return {
      id: c.id, name: c.name, zone: c.zone, type: c.type, style,
      x: z.x + (i % 7) * 5 - 15,
      y: z.y + Math.floor(i / 7) * 5 - 10,
    };
  });

  // Filtered customer markers
  const visibleCustomers = custCoords.filter(c => {
    if (c.type === "Distributor") return showDistributors;
    if (c.type === "Retailer") return showRetailers;
    if (c.type === "Corporate" || c.type === "Institutional") return showCorporate;
    return true;
  });

  // Salesman with assigned route (list of customers to visit)
  // Each salesman gets a route through 3-4 customers in their zone
  const salesmenWithRoutes = salesmen.slice(0, 6).map((s, i) => {
    const baseCity = ["Dhaka","Chattogram","Khulna","Sylhet","Rajshahi","Barishal"][i % 6];
    const base = BD_COORDS[baseCity] ?? BD_COORDS.Dhaka;
    // Assign customers in the same zone as the base city
    const routeCustomers = customers
      .filter(c => c.zone.startsWith(baseCity) || i === 0)
      .slice(0, 4)
      .map((c, j) => {
        const z = zoneToCoord(c.zone);
        return { id: c.id, name: c.name, x: z.x + (j % 3) * 8 - 8, y: z.y + Math.floor(j / 3) * 6 - 3 };
      });
    // If no customers in zone, use generic coords
    const route = routeCustomers.length > 0 ? routeCustomers : [
      { id: `gen-${i}-1`, name: "Visit 1", x: base.x + 20, y: base.y + 10 },
      { id: `gen-${i}-2`, name: "Visit 2", x: base.x - 15, y: base.y + 20 },
      { id: `gen-${i}-3`, name: "Visit 3", x: base.x + 30, y: base.y - 10 },
    ];
    return { salesman: s, base, route, routeIndex: i };
  });

  // Animated salesman positions — move along their route
  const salesmenPositions = salesmenWithRoutes.map(({ salesman, base, route, routeIndex }) => {
    if (route.length === 0) return { id: salesman.id, name: salesman.name.split(" ")[0], x: base.x, y: base.y, route, currentTarget: 0, routeProgress: 0 };
    // Cycle through route points
    const totalSteps = route.length * 8; // 8 ticks per segment
    const step = (tick + routeIndex * 5) % totalSteps;
    const segment = Math.floor(step / 8);
    const progress = (step % 8) / 8;
    const from = segment === 0 ? base : route[segment - 1];
    const to = route[segment];
    const x = from.x + (to.x - from.x) * progress;
    const y = from.y + (to.y - from.y) * progress;
    return { id: salesman.id, name: salesman.name.split(" ")[0], x, y, route, currentTarget: segment, routeProgress: progress };
  });

  // Stats
  const inTransit = activeShipments.length;
  const delivered = erp.shipments.filter(s => s.status === "Delivered").length;
  const totalCOD = activeShipments.reduce((s, sh) => s + sh.codAmount, 0);
  const distributorCount = customers.filter(c => c.type === "Distributor").length;
  const retailerCount = customers.filter(c => c.type === "Retailer").length;

  // Selected marker detail
  const [selected, setSelected] = React.useState<{ kind: "salesman" | "warehouse" | "customer" | "shipment"; id: string } | null>(null);
  const [showPOD, setShowPOD] = React.useState<string | null>(null);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide flex items-center justify-between">In Transit <Truck className="size-4 text-amber-500" /></CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold text-amber-600">{inTransit}</div></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide flex items-center justify-between">Delivered <CheckCircle2 className="size-4 text-emerald-500" /></CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold text-emerald-600">{delivered}</div></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide flex items-center justify-between">Salesmen <User className="size-4 text-blue-500" /></CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold">{salesmen.length}</div></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide flex items-center justify-between">Distributors <Store className="size-4 text-red-500" /></CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold text-red-600">{distributorCount}</div></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide flex items-center justify-between">Retailers <Store className="size-4 text-blue-500" /></CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold text-blue-600">{retailerCount}</div></CardContent></Card>
      </div>

      {/* Filter buttons */}
      <div className="flex flex-wrap gap-2">
        <FilterChip active={showWarehouses} onClick={() => setShowWarehouses(v => !v)} color="#7c3aed" label="Warehouses" count={warehouses.length} />
        <FilterChip active={showDistributors} onClick={() => setShowDistributors(v => !v)} color="#e11d48" label="Distributors" count={distributorCount} />
        <FilterChip active={showRetailers} onClick={() => setShowRetailers(v => !v)} color="#3b82f6" label="Retailers" count={retailerCount} />
        <FilterChip active={showCorporate} onClick={() => setShowCorporate(v => !v)} color="#10b981" label="Corporate/Institutional" count={customers.filter(c => c.type === "Corporate" || c.type === "Institutional").length} />
        <FilterChip active={showSalesmen} onClick={() => setShowSalesmen(v => !v)} color="#f59e0b" label="Salesmen (Live)" count={salesmen.length} />
        <FilterChip active={showRoutes} onClick={() => setShowRoutes(v => !v)} color="#06b6d4" label="Routes" count={salesmenWithRoutes.length} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Map */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2"><MapPin className="size-5" /> Bangladesh Live Map</CardTitle>
            <Badge variant="outline" className="gap-1"><Crosshair className="size-3 animate-pulse text-emerald-500" /> Live · refresh 1.5s</Badge>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <svg viewBox="0 0 420 360" className="h-96 w-full max-w-3xl" style={{ background: "linear-gradient(135deg, #dbeafe 0%, #ecfeff 100%)" }}>
                {/* Bangladesh simplified outline */}
                <path d="M 100 80 L 180 60 L 240 80 L 320 90 L 360 140 L 370 220 L 340 290 L 280 320 L 200 310 L 130 280 L 90 220 L 80 150 Z"
                  fill="#bbf7d0" stroke="#16a34a" strokeWidth="2" opacity="0.6" />
                <text x="220" y="40" textAnchor="middle" className="fill-foreground text-xs font-semibold">BANGLADESH</text>

                {/* Salesman routes (lines connecting route points) */}
                {showRoutes && showSalesmen && salesmenPositions.map(sp => (
                  <g key={`route-${sp.id}`}>
                    {/* Route line from base through all stops */}
                    <polyline
                      points={`${sp.route.length > 0 ? "" : ""}${sp.x},${sp.y} ${sp.route.map(r => `${r.x},${r.y}`).join(" ")}`}
                      fill="none" stroke="#06b6d4" strokeWidth="1" strokeDasharray="2 2" opacity="0.5"
                    />
                    {/* Route stop markers (small dots) */}
                    {sp.route.map((r, j) => (
                      <circle key={`stop-${sp.id}-${j}`} cx={r.x} cy={r.y} r="2" fill="#06b6d4" opacity="0.6" />
                    ))}
                  </g>
                ))}

                {/* Customer markers — typed by customer type */}
                {visibleCustomers.map(c => (
                  <g key={c.id} className="cursor-pointer" onClick={() => setSelected({ kind: "customer", id: c.id })}>
                    {c.style.shape === "circle" && <circle cx={c.x} cy={c.y} r="4" fill={c.style.color} />}
                    {c.style.shape === "square" && <rect x={c.x - 4} y={c.y - 4} width="8" height="8" fill={c.style.color} rx="1" />}
                    {c.style.shape === "diamond" && <polygon points={`${c.x},${c.y - 5} ${c.x + 5},${c.y} ${c.x},${c.y + 5} ${c.x - 5},${c.y}`} fill={c.style.color} />}
                    {c.style.shape === "triangle" && <polygon points={`${c.x},${c.y - 5} ${c.x + 4},${c.y + 4} ${c.x - 4},${c.y + 4}`} fill={c.style.color} />}
                    <title>{c.name} ({c.type} · {c.zone})</title>
                  </g>
                ))}

                {/* Warehouse markers */}
                {showWarehouses && warehouses.filter(w => whCoord[w.id]).map(w => (
                  <g key={w.id} className="cursor-pointer" onClick={() => setSelected({ kind: "warehouse", id: w.id })}>
                    <rect x={whCoord[w.id].x - 7} y={whCoord[w.id].y - 7} width="14" height="14" fill="#7c3aed" rx="2" stroke="#fff" strokeWidth="1" />
                    <text x={whCoord[w.id].x + 10} y={whCoord[w.id].y + 4} className="fill-foreground text-[8px] font-medium">{whCoord[w.id].label}</text>
                  </g>
                ))}

                {/* Salesman markers (animated, moving along route) */}
                {showSalesmen && salesmenPositions.map(sp => (
                  <g key={sp.id} className="cursor-pointer" onClick={() => setSelected({ kind: "salesman", id: sp.id })}>
                    <circle cx={sp.x} cy={sp.y} r="6" fill="#f59e0b" stroke="#fff" strokeWidth="2">
                      <animate attributeName="r" values="6;9;6" dur="2s" repeatCount="indefinite" />
                    </circle>
                    <text x={sp.x + 10} y={sp.y + 4} className="fill-foreground text-[8px] font-medium">{sp.name}</text>
                  </g>
                ))}

                {/* Active shipments — animated paths */}
                {activeShipments.slice(0, 4).map((sh, i) => {
                  const so = erp.salesOrders.find(s => s.id === sh.soId);
                  const cust = so ? erp.getCustomer(so.customerId) : null;
                  const to = zoneToCoord(cust?.zone);
                  const from = whCoord[erp.warehouses[0]?.id] ?? { x: 230, y: 200 };
                  const progress = ((tick + i * 5) % 20) / 20;
                  const cx = from.x + (to.x - from.x) * progress;
                  const cy = from.y + (to.y - from.y) * progress;
                  return (
                    <g key={sh.id}>
                      <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke="#06b6d4" strokeWidth="1" strokeDasharray="3 3" opacity="0.6" />
                      <circle cx={cx} cy={cy} r="5" fill="#06b6d4" stroke="#fff" strokeWidth="2" />
                      <g className="cursor-pointer" onClick={() => setShowPOD(sh.id)}>
                        <circle cx={cx} cy={cy} r="9" fill="transparent" />
                      </g>
                    </g>
                  );
                })}
              </svg>
            </div>
            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              <span className="flex items-center gap-1"><span className="size-3 rounded-sm bg-purple-700" /> Warehouse</span>
              <span className="flex items-center gap-1"><span className="size-3 rounded-sm bg-red-600" /> Distributor</span>
              <span className="flex items-center gap-1"><span className="size-3 rounded-full bg-blue-500" /> Retailer</span>
              <span className="flex items-center gap-1"><span className="size-3 bg-emerald-500" style={{ transform: "rotate(45deg)" }} /> Corporate</span>
              <span className="flex items-center gap-1"><span className="size-3 bg-violet-500" style={{ clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)" }} /> Institutional</span>
              <span className="flex items-center gap-1"><span className="size-3 rounded-full bg-amber-500" /> Salesman (live)</span>
              <span className="flex items-center gap-1"><span className="size-3 rounded-full bg-cyan-500" /> Active Shipment / Route</span>
            </div>
          </CardContent>
        </Card>

        {/* Side panel — selected marker or active shipments list */}
        <Card>
          <CardHeader><CardTitle>Active Shipments</CardTitle></CardHeader>
          <CardContent className="space-y-2 max-h-[28rem] overflow-y-auto">
            {activeShipments.length === 0 && <div className="text-muted-foreground text-sm text-center py-8">No active shipments in transit.</div>}
            {activeShipments.map(sh => {
              const so = erp.salesOrders.find(s => s.id === sh.soId);
              const cust = so ? erp.getCustomer(so.customerId) : null;
              const vehicle = erp.vehicles.find(v => v.id === sh.vehicleId);
              const driver = erp.employees.find(e => e.id === sh.driverId);
              return (
                <div key={sh.id} className="rounded-md border p-3 text-sm space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-primary">{sh.shipmentNumber}</span>
                    <Badge variant="secondary" className="gap-1"><Clock className="size-3" />{sh.status}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">{cust?.name} · {cust?.zone}</div>
                  <div className="text-xs">Driver: <span className="font-medium">{driver?.name}</span> · Vehicle: <span className="font-mono">{vehicle?.regNumber}</span></div>
                  {sh.codAmount > 0 && <div className="text-xs">COD: <span className="font-semibold text-amber-600">{formatBDT(sh.codAmount)}</span></div>}
                  <div className="flex gap-1 pt-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => setShowPOD(sh.id)}><Camera className="size-3 mr-1" />Capture POD</Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* POD Capture Dialog */}
      <PODCaptureDialog shipmentId={showPOD} onOpenChange={(v) => !v && setShowPOD(null)} />
    </div>
  );
}

// Small inline wallet icon (avoid adding another import)
function Wallet2(props: { className?: string }) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={props.className}><path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"/><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"/></svg>;
}

// ===== POD Capture Dialog =====
function PODCaptureDialog({ shipmentId, onOpenChange }: { shipmentId: string | null; onOpenChange: (v: boolean) => void }) {
  const erp = useERP();
  const { toast } = useToast();
  const [signature, setSignature] = React.useState("");
  const [cod, setCod] = React.useState("0");
  const ship = erp.shipments.find(s => s.id === shipmentId);
  React.useEffect(() => { if (ship) { setSignature(""); setCod(String(ship.codAmount)); } }, [shipmentId]);

  if (!ship) return null;
  const so = erp.salesOrders.find(s => s.id === ship.soId);
  const cust = so ? erp.getCustomer(so.customerId) : null;

  return (
    <Dialog open={!!shipmentId} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Capture Proof of Delivery</DialogTitle>
        <DialogDescription>Mark shipment {ship.shipmentNumber} as delivered with receiver signature and COD amount.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Shipment:</span><span className="font-mono">{ship.shipmentNumber}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Customer:</span><span className="font-medium">{cust?.name}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Stops:</span><span>{ship.stops}</span></div>
          </div>
          <div>
            <Label htmlFor="sig">Receiver Signature (type name)</Label>
            <Input id="sig" value={signature} onChange={e => setSignature(e.target.value)} placeholder="e.g. Rahim Uddin (Receiver)" autoFocus />
          </div>
          <div>
            <Label htmlFor="cod-amt">COD Amount Collected (৳)</Label>
            <Input id="cod-amt" type="number" value={cod} onChange={e => setCod(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => {
            if (!signature.trim()) { toast({ title: "Signature required", variant: "destructive" }); return; }
            erp.capturePOD(ship.id, signature.trim(), Number(cod) || 0);
            toast({ title: "POD Captured", description: `${ship.shipmentNumber} marked Delivered. COD: ${formatBDT(Number(cod) || 0)}. Vehicle released.` });
            onOpenChange(false);
          }}><CheckCircle2 className="size-4 mr-1" /> Confirm Delivery</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SalesmanTracking({ erp }: { erp: ERP }) {
  const salesmen = erp.employees.filter(e => e.department === "Sales" || e.department === "Logistics");
  const data = salesmen.map((s, i) => ({
    id: s.id, name: s.name, designation: s.designation,
    zone: ["Dhaka Zone-1", "Chattogram", "Khulna", "Sylhet", "Rajshahi", "Barishal"][i % 6],
    lastUpdate: `${10 + i * 3} min ago`,
    status: s.status,
    speedKmh: 12 + i * 4,
    visitsToday: 3 + (i % 4),
    ordersToday: 1 + (i % 3),
  }));
  return (
    <Card>
      <CardHeader><CardTitle>Salesman GPS Tracking</CardTitle></CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50"><tr className="border-b">
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Salesman</th>
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Designation</th>
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Zone</th>
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Last Ping</th>
              <th className="px-3 py-2 text-right text-xs font-semibold uppercase">Speed</th>
              <th className="px-3 py-2 text-right text-xs font-semibold uppercase">Visits</th>
              <th className="px-3 py-2 text-right text-xs font-semibold uppercase">Orders</th>
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Status</th>
            </tr></thead>
            <tbody>
              {data.map(s => (
                <tr key={s.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="px-3 py-2 font-medium">{s.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{s.designation}</td>
                  <td className="px-3 py-2">{s.zone}</td>
                  <td className="px-3 py-2 font-mono text-xs">{s.lastUpdate}</td>
                  <td className="px-3 py-2 text-right">{s.speedKmh} km/h</td>
                  <td className="px-3 py-2 text-right">{s.visitsToday}</td>
                  <td className="px-3 py-2 text-right">{s.ordersToday}</td>
                  <td className="px-3 py-2"><Badge variant={s.status === "Active" ? "default" : s.status === "On Field" ? "secondary" : "outline"}>{s.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function RealtimeOrders({ erp }: { erp: ERP }) {
  const orders = erp.salesOrders.filter(o => ["Approved","Picking","Dispatched","Delivered"].includes(o.status)).slice(0, 12);
  return (
    <Card>
      <CardHeader><CardTitle>Real-time Order Stream</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-2">
          {orders.map(o => {
            const cust = erp.getCustomer(o.customerId);
            const ship = erp.shipments.find(s => s.soId === o.id);
            return (
              <div key={o.id} className="flex items-center gap-3 rounded-md border p-3">
                <Package className="text-primary size-5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{o.soNumber} — {cust?.name}</p>
                  <p className="text-muted-foreground text-xs">{o.orderDate} · {formatBDT(o.total)} · {cust?.zone}</p>
                </div>
                {ship && <Badge variant="outline" className="font-mono text-xs">{ship.shipmentNumber}</Badge>}
                <Badge variant={o.status === "Delivered" ? "default" : o.status === "Dispatched" ? "secondary" : "outline"}>{o.status}</Badge>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function ZoneManagement({ erp }: { erp: ERP }) {
  const zones = Array.from(new Set(erp.customers.map(c => c.zone)));
  const zoneStats = zones.map(z => {
    const custs = erp.customers.filter(c => c.zone === z);
    const outstanding = custs.reduce((s, c) => s + c.outstanding, 0);
    const blocked = custs.filter(c => c.status === "Blocked").length;
    const coords = zoneToCoord(z);
    return { zone: z, customers: custs.length, outstanding, blocked, x: coords.x, y: coords.y };
  });
  return (
    <Card>
      <CardHeader><CardTitle>Zone Management</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {zoneStats.map(z => (
            <div key={z.zone} className="rounded-md border p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Navigation className="size-4 text-primary" /><span className="font-semibold">{z.zone}</span></div>
                {z.blocked > 0 && <Badge variant="destructive">{z.blocked} blocked</Badge>}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div><p className="text-muted-foreground text-xs">Customers</p><p className="font-semibold">{z.customers}</p></div>
                <div><p className="text-muted-foreground text-xs">Outstanding</p><p className="font-semibold text-destructive">{formatBDT(z.outstanding)}</p></div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ===== Filter Chip component for map marker type toggles =====
function FilterChip({ active, onClick, color, label, count }: {
  active: boolean; onClick: () => void; color: string; label: string; count: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
        active ? "border-foreground/20 bg-foreground/5 text-foreground" : "border-transparent bg-muted/50 text-muted-foreground line-through opacity-60"
      }`}
    >
      <span className="size-2.5 rounded-full" style={{ background: color }} />
      {label} ({count})
    </button>
  );
}
